import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    addActivity,
    addColumn,
    addGroup,
    deleteActivity,
    deleteColumn,
    deleteGroup,
    moveActivityToGroup,
    moveActivityWithinGroup,
    moveColumn,
    moveGroup,
    setAssignment,
    updateActivityName,
    updateColumnLabel,
    updateGroupName,
    createEmptyRaciMatrix,
} from './raciMatrixModel';
import { fetchRaciMatrix, persistRaciMatrix } from './raciMatrixStorage';
import { setRaciMatrixLocal } from '../../redux/raciMatrixSlice';

const AUTOSAVE_DELAY_MS = 600;

export const useRaciMatrix = projectId => {
    const dispatch = useDispatch();
    const { matrix: storeMatrix, canEdit: storeCanEdit, saveStatus, status } = useSelector(state => state.raciMatrix || {});

    const readOnly = storeCanEdit === false;
    const [matrix, setMatrix] = useState(createEmptyRaciMatrix());
    const [loadError, setLoadError] = useState(null);
    const [initialLoading, setInitialLoading] = useState(true);
    const saveTimerRef = useRef(null);
    const matrixRef = useRef(matrix);

    useEffect(() => {
        matrixRef.current = matrix;
    }, [matrix]);

    useEffect(() => {
        if (storeMatrix && status === 'succeeded') {
            setMatrix(storeMatrix);
        }
    }, [storeMatrix, status]);

    useEffect(() => {
        if (!projectId) {
            setInitialLoading(false);
            setLoadError(null);
            setMatrix(createEmptyRaciMatrix());
            return undefined;
        }
        let cancelled = false;

        const load = async () => {
            setLoadError(null);
            setInitialLoading(true);
            try {
                const { matrix: loaded } = await fetchRaciMatrix(projectId);
                if (!cancelled) {
                    setMatrix(loaded);
                    dispatch(setRaciMatrixLocal(loaded));
                }
            } catch (error) {
                if (!cancelled) {
                    setLoadError(error?.message || 'Failed to load RACI matrix');
                }
            } finally {
                if (!cancelled) {
                    setInitialLoading(false);
                }
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [projectId, dispatch]);

    const scheduleSave = useCallback(() => {
        if (readOnly || !projectId) return;
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        saveTimerRef.current = setTimeout(async () => {
            try {
                await persistRaciMatrix(projectId, matrixRef.current);
            } catch {
                // saveStatus handled in redux slice
            }
        }, AUTOSAVE_DELAY_MS);
    }, [projectId, readOnly]);

    useEffect(
        () => () => {
            if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        },
        [],
    );

    const commit = useCallback(
        updater => {
            if (readOnly) return null;
            setMatrix(prev => {
                const next = typeof updater === 'function' ? updater(prev) : updater;
                dispatch(setRaciMatrixLocal(next));
                return next;
            });
            scheduleSave();
            return null;
        },
        [readOnly, scheduleSave, dispatch],
    );

    const retrySave = useCallback(async () => {
        if (!projectId) return;
        try {
            await persistRaciMatrix(projectId, matrixRef.current);
        } catch {
            // error state in redux
        }
    }, [projectId]);

    const reload = useCallback(async () => {
        if (!projectId) return;
        setLoadError(null);
        try {
            const { matrix: loaded } = await fetchRaciMatrix(projectId);
            setMatrix(loaded);
            dispatch(setRaciMatrixLocal(loaded));
        } catch (error) {
            setLoadError(error?.message || 'Failed to load RACI matrix');
        }
    }, [projectId, dispatch]);

    return {
        matrix,
        saveStatus,
        loadStatus: status,
        initialLoading,
        loadError,
        canEdit: storeCanEdit !== false,
        retrySave,
        reload,
        setAssignment: (activityId, columnId, value) =>
            commit(prev => setAssignment(prev, activityId, columnId, value)),
        addRow: (groupId = null) => {
            if (readOnly) return null;
            const result = addActivity(matrixRef.current, { name: '', groupId });
            setMatrix(result.matrix);
            dispatch(setRaciMatrixLocal(result.matrix));
            return result.activityId;
        },
        addGroup: name => commit(prev => addGroup(prev, name)),
        addColumn: label => commit(prev => addColumn(prev, label)),
        renameActivity: (activityId, name) => commit(prev => updateActivityName(prev, activityId, name)),
        deleteActivity: activityId => commit(prev => deleteActivity(prev, activityId)),
        moveActivityUp: activityId => commit(prev => moveActivityWithinGroup(prev, activityId, 'up')),
        moveActivityDown: activityId => commit(prev => moveActivityWithinGroup(prev, activityId, 'down')),
        moveActivityToGroup: (activityId, groupId) => commit(prev => moveActivityToGroup(prev, activityId, groupId)),
        renameColumn: (columnId, label) => commit(prev => updateColumnLabel(prev, columnId, label)),
        deleteColumn: columnId => commit(prev => deleteColumn(prev, columnId)),
        moveColumnLeft: columnId => commit(prev => moveColumn(prev, columnId, 'left')),
        moveColumnRight: columnId => commit(prev => moveColumn(prev, columnId, 'right')),
        renameGroup: (groupId, name) => commit(prev => updateGroupName(prev, groupId, name)),
        deleteGroup: groupId => commit(prev => deleteGroup(prev, groupId)),
        moveGroupUp: groupId => commit(prev => moveGroup(prev, groupId, 'up')),
        moveGroupDown: groupId => commit(prev => moveGroup(prev, groupId, 'down')),
    };
};
